import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, ShieldCheck, AlertTriangle } from "lucide-react";
import { Btn, Badge, Spinner } from "@/components/ui-kit";
import api from "@/lib/api";

const pdf = async (r) => {
  try {
    const res = await api.get(`/legal/acceptances/${r.id}/pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    Object.assign(document.createElement("a"), { href: url, download: `PranIQ_Clinician_Agreement_v${r.version}_${r.id.slice(0, 8)}.pdf` }).click();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  } catch { toast.error("Could not download the PDF"); }
};

const Yes = ({ on }) => <Badge tone={on ? "emerald" : "slate"}>{on ? "Ticked" : "Not ticked"}</Badge>;

function Row({ r }) {
  return (
    <div className="rounded-xl border border-slate-200 p-4" data-testid={`ops-acc-${r.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <b className="text-[13.5px] text-slate-900">Version {r.version}</b>
        <Badge tone={r.kind === "signup" ? "teal" : "sky"}>{r.kind === "signup" ? "Sign-up" : "Re-acceptance"}</Badge>
        <span className="text-[12.5px] text-slate-600">{r.accepted_at_ist}</span>
        <Btn size="sm" className="ml-auto" onClick={() => pdf(r)} data-testid={`ops-acc-pdf-${r.id}`}><Download size={13} /> PDF</Btn>
      </div>
      <div className="mt-3 grid gap-x-6 gap-y-1.5 text-[12px] sm:grid-cols-2">
        <div><span className="text-slate-400">Name · qualification · reg. no:</span> {r.name} · {r.qualification || "—"} · {r.registration_no || "—"}</div>
        <div><span className="text-slate-400">UTC:</span> {r.accepted_at_utc}</div>
        <div><span className="text-slate-400">IP · device:</span> {r.ip || "—"} · {r.device?.device_type} · {r.device?.os} · {r.device?.browser}</div>
        <div><span className="text-slate-400">Verification:</span> {r.verification?.method === "email_otp" ? "Email OTP" : "Signed-in session"} · {r.verification?.verified_at?.slice(0, 19).replace("T", " ")}</div>
        <div className="break-all sm:col-span-2"><span className="text-slate-400">Document hash (SHA-256):</span> {r.doc_hash}</div>
        <div className="flex flex-wrap items-center gap-1.5 sm:col-span-2"><span className="text-slate-400">A.1</span><Yes on={r.checkboxes?.a1} /><span className="text-slate-400">Schedule B</span><Yes on={r.checkboxes?.schedule_b} /><span className="text-slate-400">A.2 marketing</span><Yes on={r.checkboxes?.a2} /><span className="text-slate-400">· {r.language}</span></div>
        {r.retain_until && <div className="sm:col-span-2 text-amber-700">Account closed {r.account_closed_at?.slice(0, 10)} · kept until {r.retain_until.slice(0, 10)}</div>}
      </div>
    </div>
  );
}

export default function LegalAcceptances({ cid }) {
  const [rows, setRows] = useState(null);
  const [st, setSt] = useState(null);
  useEffect(() => {
    api.get("/admin/legal/acceptances", { params: { account_id: cid } }).then((r) => setRows(r.data));
    api.get(`/admin/legal/status/${cid}`).then((r) => setSt(r.data));
  }, [cid]);
  if (!rows) return <div className="py-8 grid place-items-center"><Spinner className="w-5 h-5" /></div>;
  return (
    <div className="space-y-3" data-testid="ops-legal-acceptances">
      {st && (
        <div className={`flex items-center gap-2 rounded-xl px-3.5 py-2.5 text-[12.5px] ${st.required ? "bg-amber-50 text-amber-800" : "bg-emerald-50 text-emerald-800"}`} data-testid="ops-legal-status">
          {st.required ? <AlertTriangle size={14} /> : <ShieldCheck size={14} />}
          {st.required ? `Has not accepted the current agreement (v${st.current_version}) — new orders can't be assigned until they do.` : `Current agreement v${st.current_version} accepted.`}
        </div>
      )}
      {rows.length === 0 ? <p className="text-[12.5px] text-slate-500" data-testid="ops-legal-empty">No acceptance recorded yet.</p> : rows.map((r) => <Row key={r.id} r={r} />)}
    </div>
  );
}
